import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Heart, Edit, FolderPlus, Trash2, Eye, ChevronDown } from "lucide-react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { Card, CardCover, CardTitle, type CardProps } from "../primitives/Card";
import { Button } from "../primitives/Button";
import { Badge } from "../primitives/Badge";
import {
  ExperienceTypeBadge,
  getExperienceTypeMeta,
  type ExperienceType,
  type ExperienceTypeTone,
} from "./ExperienceTypeBadge";
import { VisibilityBadge } from "./VisibilityBadge";
import { Skeleton } from "../primitives/Skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../primitives/Tooltip";
import { AikoMark } from "./AikoMark";
import { Truncate } from "./Truncate";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../primitives/DropdownMenu";

/**
 * ExperienceCard — community-card-style entity card for AI Experiences
 * (Quest / Video / Note / Podcast / Expert). Image on top with overlaid
 * type tag + draft chip + visibility, content below with hard 2-line
 * clamps for view stability, primary CTA + dropdown chevron in the footer.
 *
 * AIKO whispers a contextual hint when the title or description is hovered
 * (subtle helper, 400ms hover delay, dismissable). Pass `aikoTooltip={false}`
 * to opt out, or pass a custom message string / node.
 *
 * The live card is the brand `Card`, so it INHERITS the Onyx hex-notch surface
 * (`nx-s` silhouette + clip-safe `.elev-card` drop-shadow + continuous `nx-rim`
 * keyline ring) — we do NOT re-apply the recipe or a second keyline here. The
 * `.Skeleton` renders its own placeholder surface, so it adopts the recipe
 * directly: `nx-s` + `bg-card` + `.elev-card` + an `nx-rim` ring, never the v1
 * `border` + outer `box-shadow` + `rounded-rect` (both sliced by the clip, #158).
 */

// `ExperienceType` is owned by the ExperienceTypeBadge primitive — re-export
// so consumers don't have to import from two places.
export type { ExperienceType };

export interface ExperienceCardCreator {
  name?: string;
  username?: string;
  avatar?: string;
}

export interface ExperienceCardEntity {
  id: string;
  title: string;
  type: ExperienceType;
  coverImage?: string;
  studioName?: string;
  studioLogo?: string;
  studioSlug?: string;
  creatorName?: string;
  creatorUsername?: string;
  creatorAvatar?: string;
  likeCount?: number;
  status?: "published" | "draft";
  visibility?: "public" | "private" | "team";
  description?: string;
  tags?: string[];
}

export interface ExperienceMenuAction {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  destructive?: boolean;
}

export interface ExperienceCardProps extends Omit<
  CardProps,
  "onClick" | "loading" | "children" | "view" | "hover"
> {
  experience: ExperienceCardEntity;
  /** The deployed resource ID (forwarded to consumer-side analytics if needed). */
  deployedResourceId?: string;
  view?: "grid" | "list";
  mode: "manage" | "explore" | "community";
  hover?: "lift" | "glow" | "none";

  /** Primary card action — also fires on whole-card click. */
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCollection?: () => void;

  isLiked?: boolean;
  onLikeToggle?: (liked: boolean) => void;

  /** Override the default type-tag color tone. */
  tagTone?: ExperienceTypeTone;

  /** AIKO hover-tooltip helper. Pass `false` to opt out. */
  aikoTooltip?: React.ReactNode | false;

  /**
   * Card density. `comfortable` (default) renders exactly as before. `editorial`
   * is the blog-listing feel: a larger title, a 3-line description, and no
   * action footer (the whole card is the click target via `onView`).
   */
  density?: ExperienceCardDensity;
}

/** Card density: the default app card vs the blog-listing "editorial" look. */
export type ExperienceCardDensity = "comfortable" | "editorial";

const formatLikes = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : String(n);

function ExperienceCoverFallback({ type }: { type: ExperienceType }) {
  const meta = getExperienceTypeMeta(type);
  const Icon = meta.Icon;
  return (
    <div
      className={cn(
        "relative h-full w-full bg-gradient-to-br flex items-center justify-center",
        meta.gradient,
      )}
    >
      <div
        aria-hidden
        className="absolute -bottom-1/3 -right-1/4 size-[120%] rounded-full bg-white/10 blur-3xl"
      />
      <Icon className="relative size-14 sm:size-16 text-foreground/40" strokeWidth={1.25} />
    </div>
  );
}

function AikoTooltip({
  message,
  children,
}: {
  message: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="flex items-start gap-2">
            <AikoMark size="xs" aria-hidden />
            <span className="text-xs leading-relaxed">{message}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type ExperienceCardComponent = React.ForwardRefExoticComponent<
  ExperienceCardProps & React.RefAttributes<HTMLDivElement>
> & {
  Skeleton: typeof ExperienceCardSkeleton;
};

const ExperienceCardImpl = React.forwardRef<HTMLDivElement, ExperienceCardProps>(
  (
    {
      experience,
      view = "grid",
      mode,
      hover = "lift",
      onView,
      onEdit,
      onDelete,
      onCollection,
      isLiked = false,
      onLikeToggle,
      tagTone,
      aikoTooltip,
      density = "comfortable",
      className,
      ...rest
    },
    ref,
  ) => {
    const editorial = density === "editorial";
    const {
      title,
      type,
      coverImage,
      studioName,
      creatorName,
      creatorUsername,
      likeCount = 0,
      status,
      visibility,
      description,
      tags,
    } = experience;

    const meta = getExperienceTypeMeta(type);
    const visKey = (visibility ?? "").toLowerCase();
    const showVisibility = Boolean(visKey);

    const aikoMessage =
      aikoTooltip === false
        ? null
        : (aikoTooltip ??
          (likeCount > 0
            ? `Open this ${meta.label.toLowerCase()} — ${formatLikes(likeCount)} ${
                likeCount === 1 ? "like" : "likes"
              }${creatorName ? ` from ${creatorName}` : ""}.`
            : `Open this ${meta.label.toLowerCase()}${creatorName ? ` by ${creatorName}` : ""}.`));

    const stopProp = (e: React.MouseEvent) => e.stopPropagation();
    const fire = (handler?: () => void) => (e: React.MouseEvent | Event) => {
      if ("stopPropagation" in e) e.stopPropagation();
      handler?.();
    };

    const manageMenuItems: Array<React.ReactNode> = [];
    if (mode === "manage") {
      if (onView) {
        manageMenuItems.push(
          <DropdownMenuItem key="view" onSelect={() => onView()}>
            <Eye />
            <span>View</span>
          </DropdownMenuItem>,
        );
      }
      if (onDelete) {
        if (manageMenuItems.length > 0) manageMenuItems.push(<DropdownMenuSeparator key="sep" />);
        manageMenuItems.push(
          <DropdownMenuItem key="delete" variant="destructive" onSelect={() => onDelete()}>
            <Trash2 />
            <span>Delete</span>
          </DropdownMenuItem>,
        );
      }
    }

    return (
      <Card
        ref={ref}
        view={view}
        hover={hover}
        onClick={onView}
        className={cn(view === "grid" && "h-full", onView && "cursor-pointer", className)}
        {...rest}
      >
        <CardCover
          view={view}
          aspect="16/9"
          src={coverImage}
          alt={`${title || "Experience"} cover`}
          fallback={<ExperienceCoverFallback type={type} />}
          pixelEffect={view === "grid"}
        >
          {/* Bottom shade — anchors the chip strip on any cover. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
          />

          {/* Top-right: explore-mode like / save floating buttons (interactive
              tap targets — kept off the bottom-left badge strip so they don't
              scroll out of reach when the strip overflows). */}
          {mode !== "manage" && (onCollection || onLikeToggle) ? (
            <div className="absolute top-2 right-2 flex items-center gap-1.5">
              {onCollection ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 rounded-full bg-background/85 backdrop-blur-sm shadow-sm hover:bg-background"
                  onClick={fire(onCollection)}
                  aria-label="Add to collection"
                >
                  <FolderPlus />
                </Button>
              ) : null}
              {onLikeToggle ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 rounded-full bg-background/85 backdrop-blur-sm shadow-sm hover:bg-background"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLikeToggle(!isLiked);
                  }}
                  aria-pressed={isLiked}
                  aria-label={isLiked ? "Unlike" : "Like"}
                >
                  <Heart
                    className={cn("transition-colors", isLiked && "fill-current text-rose-500")}
                  />
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* Bottom-left scroll strip — type tag + visibility + draft. Single
              horizontal row with right-edge mask fade so a packed strip on a
              narrow card hints there's more to scroll. */}
          <div className="absolute inset-x-3 bottom-3 pointer-events-none">
            <div
              className={cn(
                "flex items-center gap-2 overflow-x-auto pointer-events-auto",
                "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                "[mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                "[-webkit-mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                "pr-8",
              )}
            >
              <ExperienceTypeBadge
                type={type}
                tone={tagTone}
                appearance="overlay"
                size="sm"
                className="flex-shrink-0 shadow-sm"
              />
              {showVisibility ? (
                <VisibilityBadge
                  visibility={visKey}
                  appearance="overlay"
                  size="sm"
                  className="flex-shrink-0 shadow-sm"
                />
              ) : null}
              {status === "draft" ? (
                <Badge
                  variant="warning"
                  className="flex-shrink-0 bg-background/85 backdrop-blur-sm shadow-sm"
                >
                  Draft
                </Badge>
              ) : null}
            </div>
          </div>
        </CardCover>

        {/* Content section. `flex-1` so it absorbs remaining card height
            (outer Card has `h-full` in grid view), keeping the action footer
            pinned to the bottom across siblings with varied content lengths. */}
        <div
          className={cn(
            "flex-1 flex flex-col gap-3 min-w-0",
            view === "grid" ? "p-4 sm:p-5" : "py-1 px-1",
          )}
        >
          {aikoMessage ? (
            <AikoTooltip message={aikoMessage}>
              <CardTitle
                as="h3"
                size={editorial ? "lg" : view === "grid" ? "md" : "sm"}
                clamp={view === "list" ? 1 : 2}
              >
                {title}
              </CardTitle>
            </AikoTooltip>
          ) : (
            <CardTitle
              as="h3"
              size={editorial ? "lg" : view === "grid" ? "md" : "sm"}
              clamp={view === "list" ? 1 : 2}
            >
              {title}
            </CardTitle>
          )}

          {/* Description — auto-tooltip on overflow via `<Truncate>`. */}
          <Truncate
            as="p"
            lines={editorial ? 3 : view === "list" ? 1 : 2}
            className={cn(
              "text-sm leading-relaxed text-muted-foreground",
              editorial ? "min-h-[3.9rem]" : view === "grid" && "min-h-[2.6rem]",
            )}
            tooltipBody={typeof description === "string" ? description : undefined}
          >
            {description ?? <span className="invisible">·</span>}
          </Truncate>

          {/* Tags row — first 3 visible, "+N" overflow chip if more. Hidden
              when no tags. Uses outline Badges for visual quietness. */}
          {tags && tags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 -mt-1">
              {tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" size="sm" className="font-normal">
                  {tag}
                </Badge>
              ))}
              {tags.length > 3 ? (
                <Badge variant="outline" size="sm" className="font-normal">
                  +{tags.length - 3}
                </Badge>
              ) : null}
            </div>
          ) : null}

          {/* Meta row. `min-h-[1.25rem]` keeps the row at one line of height
              even when the content row wraps, so the action footer Y stays
              uniform across siblings. */}
          <div className="flex min-h-[1.25rem] items-center gap-x-3 gap-y-1 flex-wrap text-xs sm:text-sm text-muted-foreground">
            {studioName ? (
              <Truncate as="span" className="max-w-[40%]">
                {studioName}
              </Truncate>
            ) : null}
            {creatorName ? (
              <Truncate as="span" className="max-w-[40%]">
                by {creatorName}
              </Truncate>
            ) : creatorUsername ? (
              <Truncate as="span" className="max-w-[40%]">
                @{creatorUsername}
              </Truncate>
            ) : null}
            {likeCount > 0 ? (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <Heart className="size-3.5" />
                {formatLikes(likeCount)}
              </span>
            ) : null}
          </div>

          {/* Action footer. `mt-auto` pins it to the bottom of the flex column.
              Editorial density drops it — the whole card is the click target. */}
          {view === "grid" && !editorial ? (
            <div className="mt-auto flex items-center gap-2 pt-1" onClick={stopProp}>
              {mode === "manage" && onEdit ? (
                <>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={fire(onEdit)}
                    aria-label={`Edit ${title}`}
                  >
                    <Edit />
                    <span>Edit</span>
                  </Button>
                  {manageMenuItems.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="outline"
                          className="size-9 flex-shrink-0"
                          aria-label={`${title || "Experience"} more actions`}
                        >
                          <ChevronDown />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">{manageMenuItems}</DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </>
              ) : mode !== "manage" && onView ? (
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={fire(onView)}
                  aria-label={`Open ${title}`}
                >
                  <span>Open</span>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
    );
  },
);
ExperienceCardImpl.displayName = "ExperienceCard";

/* -------------------------------------------------------------------------- */
/* ExperienceCard.Skeleton                                                    */
/* -------------------------------------------------------------------------- */

interface ExperienceCardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  view?: "grid" | "list";
  /** Match the density of the cards being loaded (see ExperienceCard). */
  density?: ExperienceCardDensity;
}

export const ExperienceCardSkeleton = React.forwardRef<HTMLDivElement, ExperienceCardSkeletonProps>(
  ({ view = "grid", density = "comfortable", className, ...props }, ref) => {
    const editorial = density === "editorial";
    if (view === "list") {
      return (
        // Placeholder surface — adopts the notch recipe directly (the live card
        // inherits it from Card, but the skeleton renders its own box): `nx-s`
        // + `bg-card` + clip-safe `.elev-card` drop-shadow + an `nx-rim` ring,
        // never a `border` / outer `box-shadow` (both sliced by the clip, #158).
        <div
          ref={ref}
          className={cn(
            nx.s,
            "relative bg-card elev-card p-3 sm:p-4 flex items-start gap-4",
            className,
          )}
          {...props}
        >
          <span
            aria-hidden
            className="nx-rim pointer-events-none absolute inset-0 [--nx-rim:hsl(var(--border))]"
            style={{ clipPath: NX_RING_CLIP.s }}
          />
          <Skeleton
            animation="shimmer"
            className="aspect-video w-32 sm:w-40 lg:w-48 rounded-lg flex-shrink-0"
          />
          <div className="flex-1 min-w-0 flex flex-col gap-2 py-1">
            <Skeleton animation="shimmer" className="h-5 w-20 rounded-full" />
            <Skeleton animation="shimmer" className="h-5 w-3/4 rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
          </div>
        </div>
      );
    }
    return (
      // Same notch recipe as the list variant. `overflow-hidden` keeps the cover
      // skeleton inside the `nx-s` silhouette (incl. the top-right chamfer), so
      // the cover carries no radius of its own — the notch clip shapes its top.
      <div
        ref={ref}
        className={cn(
          nx.s,
          "relative bg-card elev-card overflow-hidden flex flex-col",
          className,
        )}
        {...props}
      >
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0 z-[1] [--nx-rim:hsl(var(--border))]"
          style={{ clipPath: NX_RING_CLIP.s }}
        />
        <Skeleton animation="shimmer" className="aspect-video w-full rounded-none" />
        <div className="p-4 sm:p-5 flex flex-col gap-3">
          <Skeleton animation="shimmer" className={cn("rounded-md", editorial ? "h-6 w-3/4" : "h-5 w-3/5")} />
          <div className="flex flex-col gap-1.5">
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-4/5 rounded-md" />
            {editorial ? <Skeleton animation="shimmer" className="h-4 w-3/5 rounded-md" /> : null}
          </div>
          <Skeleton animation="shimmer" className="h-3 w-1/2 rounded-md" />
          {/* Editorial cards have no action footer, so the skeleton drops it too. */}
          {editorial ? null : (
            <div className="flex items-center gap-2 pt-1">
              <Skeleton animation="shimmer" className="h-9 flex-1 rounded-full" />
              <Skeleton animation="shimmer" className="size-9 rounded-full" />
            </div>
          )}
        </div>
      </div>
    );
  },
);
ExperienceCardSkeleton.displayName = "ExperienceCard.Skeleton";

/* -------------------------------------------------------------------------- */
/* ExperienceCardGrid                                                         */
/* -------------------------------------------------------------------------- */

export interface ExperienceCardGridProps extends React.HTMLAttributes<HTMLDivElement> {
  items: ExperienceCardEntity[];
  renderItem: (experience: ExperienceCardEntity, index: number) => React.ReactNode;
  animate?: boolean;
  /**
   * Column count at xl breakpoint. Mirrors CollectionCardGrid's API so the
   * three card grids (Studio / Experience / Collection) compose with parity
   * across surfaces. Default `"3"` (1 / 2 / 2 / 3 across sm / md / lg / xl).
   */
  columns?: "2" | "3" | "4";
  loading?: boolean;
  skeletonCount?: number;
  /** Density of the cards in this grid; the loading skeletons match it. */
  density?: ExperienceCardDensity;
}

export const ExperienceCardGrid = React.forwardRef<HTMLDivElement, ExperienceCardGridProps>(
  (
    {
      items,
      renderItem,
      animate = true,
      columns = "3",
      className,
      loading = false,
      skeletonCount = 6,
      density = "comfortable",
      ...props
    },
    ref,
  ) => {
    // Canonical breakpoints, identical across StudioCardGrid + ExperienceCardGrid
    // + CollectionCardGrid so the three card surfaces inherit one grid rhythm.
    const grid =
      columns === "2"
        ? "grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
        : columns === "4"
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6"
          : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6";
    if (loading) {
      return (
        <motion.div
          ref={ref}
          className={cn(grid, className)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          {...(props as React.ComponentProps<typeof motion.div>)}
        >
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <ExperienceCardSkeleton key={i} density={density} />
          ))}
        </motion.div>
      );
    }
    if (!animate) {
      return (
        <div ref={ref} className={cn(grid, className)} {...props}>
          {items.map((e, i) => (
            <React.Fragment key={e.id}>{renderItem(e, i)}</React.Fragment>
          ))}
        </div>
      );
    }
    return (
      <div ref={ref} className={cn(grid, className)} {...props}>
        <AnimatePresence mode="popLayout" initial={false}>
          {items.map((e, i) => (
            <motion.div
              key={e.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                layout: { type: "spring", stiffness: 260, damping: 30 },
                opacity: { duration: 0.25, ease: "easeOut" },
                y: { duration: 0.25, ease: "easeOut" },
                delay: Math.min(i * 0.03, 0.18),
              }}
            >
              {renderItem(e, i)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );
  },
);
ExperienceCardGrid.displayName = "ExperienceCardGrid";

export const ExperienceCard = ExperienceCardImpl as ExperienceCardComponent;
ExperienceCard.Skeleton = ExperienceCardSkeleton;
